import type { TridentCampaignDetails } from 'types/trident-campaigns';
import React, { Component } from 'react';
import _ from 'lodash';
import i18n from 'i18n-js';
import type { City, Country } from 'types/country';
import CDatePicker from 'components/custom/CDatePicker';
import FormInputWithError from 'components/form/FormInputWithError';
import { Grid, Form, Checkbox, Header, Button, Message, Accordion, Label, Divider, Icon, Segment, Popup } from 'semantic-grab-ui/src';
import LimitsTable from './LimitsTable';
import formComponent from 'components/decorators/form_component';
import SubScenarioForm from './SubScenarioForm';
import './TreatmentForm.scss';
import Toastr from 'utils/toastr-util';
import { limitTypes } from 'data/trident-campaigns';
import { targetUserTypes } from 'data/trident-types';
import { validateLHS } from 'omega-rtc/components/rule-builder/utils';

type Props = {
  model: TridentCampaignDetails,
  cities: Array<City>,
  country: Country,
  canActivate: boolean,
  currentNode: string,
  parentNode: string,
  parentKeyList: Array<string>,
  onModelChange: (_?: Object) => void,
  handleCloseModal: Function,
  handleInputChange: Function,
  handleControlBlur: Function,
  isAdd: boolean,
  handleDeleteNode: Function,
  addDefaultExperiment: Function,
  isValid: Function,
  error: any,
  listRuleTypes: Object,
};

const MAX_SUBSCENARIOS = 10; // maximum number of sub scenarios per node

@formComponent()
export default class ScenarioForm extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      originalModel: this.props.model,
      currentNode: this.props.currentNode,
      parentNode: this.props.parentNode,
      subScenarioIndex: 0,
    };
    this.form = {};
  }

  componentDidMount() {
    if (this.props.isAdd) this.doSyncDates();
  }

  handleCheckSameDatesChange = (event: any, data: any) => {
    const { checked } = data;
    if (checked === true) {
      this.doSyncDates();
    } else {
      const updatedModel = _.cloneDeep(this.props.model);
      _.set(updatedModel, `nodes.${this.state.currentNode}.data.meta.isSameDateChecked`, false);
      this.props.onModelChange(updatedModel);
    }
  };

  doSyncDates = () => {
    const updatedModel = _.cloneDeep(this.props.model);
    _.set(updatedModel, `nodes.${this.state.currentNode}.data.meta.startTime`, this.props.model.startTime);
    _.set(updatedModel, `nodes.${this.state.currentNode}.data.meta.endTime`, this.props.model.endTime);
    _.set(updatedModel, `nodes.${this.state.currentNode}.data.meta.isSameDateChecked`, true);
    this.props.onModelChange(updatedModel);
  };

  // isEdit returns true if there are nodes following the scenario already
  isEdit = (): boolean => {
    return !_.isEmpty(_.get(this.props.model.nodes, `${this.state.currentNode}.children`, []));
  };

  handleLastOccurrenceChange = (event: any, data: any) => {
    const { checked } = data;
    const updatedModel = _.cloneDeep(this.props.model);
    if (checked === true) {
      const numberForLastOccurrence = _.get(updatedModel, 'recordLastOccurrenceKey') + 1;
      const occurrenceKey = 'T'.concat(numberForLastOccurrence.toString());
      _.set(updatedModel, `nodes.${this.state.currentNode}.recordLastOccurrenceKey`, occurrenceKey);
      _.set(updatedModel, 'recordLastOccurrenceKey', numberForLastOccurrence);
    } else {
      delete _.get(updatedModel, `nodes.${this.state.currentNode}`).recordLastOccurrenceKey;
    }
    this.props.onModelChange(updatedModel);
  };

  cancelScenario = () => {
    if (this.props.isAdd) {
      // eslint-disable-next-line no-alert
      const confirmValue = confirm('Cancel will delete this inactive node. Are you sure to delete this?');
      if (!confirmValue) return;
      this.props.handleDeleteNode(this.state.parentNode, this.state.currentNode, this.props.scenarioIndex);
    } else {
      this.props.onModelChange(this.state.originalModel);
    }
    this.props.handleCloseModal();
  };

  saveScenario = () => {
    // validate limit types and limit vars
    const { listRuleTypes, model } = this.props;
    const allowedLimitTypes = this.getAllowedLimitTypes();
    const allowedLimitVars = this.getAllowedLimitVars();
    const limits = _.get(this.props.model, `nodes.${this.state.currentNode}.limits`, []);
    for (let i = 0; i < limits.length; i += 1) {
      if (_.findIndex(allowedLimitTypes, l => l.key === limits[i].per) === -1) {
        Toastr.error(`${limits[i].per} limit is not supported by all the scenarios in the node, please remove it`);
        return;
      }
      if (!_.isEmpty(limits[i].variableName) && _.findIndex(allowedLimitVars, l => l.variableName === limits[i].variableName) === -1) {
        Toastr.error(`Dynamic variable ${limits[i].variableName} is not supported by all the scenarios in the node, please remove it`);
        return;
      }
      if (limits[i].per === limitTypes.dynamicValue || limits[i].per === limitTypes.dynamicValuePerUser) {
        if (_.isEmpty(limits[i].variableName)) {
          Toastr.error('No variable is selected for the variable limit!');
          return;
        }
      }
    }

    // validate scenario event types and condition
    const scenarios = _.get(this.props.model, `nodes.${this.state.currentNode}.data.scenarios`, []);
    for (let i = 0; i < scenarios.length; i += 1) {
      const subScenario = scenarios[i];
      if (_.isEmpty(subScenario.eventType)) {
        Toastr.error('Scenario event type cannot be empty.');
        return;
      }

      if (!_.isEmpty(subScenario.condition)) {
        const eventTypeConfig = _.get(listRuleTypes, model.targetUserType, []).find(
          eventData => _.get(eventData, 'eventType', '') === subScenario.eventType,
        );

        const errMsg = validateLHS(_.get(eventTypeConfig, 'ruleTypes', []), subScenario.condition);
        if (!_.isEmpty(errMsg)) {
          Toastr.error(errMsg);
          return;
        }
      }
    }

    if (this.props.isValid()) {
      // auto-holdback feature
      if (!this.isEdit() && (this.props.model.targetUserType === targetUserTypes.pax || this.props.model.targetUserType === targetUserTypes.dax) && _.get(this.props.model, `nodes.${this.state.currentNode}.autoHoldbackEnabled`, false)) {
        this.props.addDefaultExperiment(this.state.currentNode);
      }
      this.props.handleCloseModal();
    } else {
      Toastr.error('There are errors in scenario form.');
    }
  };

  // getAllowedLimitVars returns intersection of the limit vars of all the selected event types
  getAllowedLimitVars = () => {
    const { model, listRuleTypes } = this.props;
    const allEventTypes = _.get(model, `nodes.${this.state.currentNode}.data.scenarios`, []).map(s => s.eventType);
    let allowedLimitVars = [];
    _.map(allEventTypes, eventType => {
      const eventTypeConfig = _.get(listRuleTypes, model.targetUserType, []).find(
        eventData => _.get(eventData, 'eventType', '') === eventType,
      );
      const limitVars = _.get(eventTypeConfig, 'limitVars', []);
      if (allowedLimitVars.length === 0) {
        allowedLimitVars = limitVars;
      } else {
        allowedLimitVars = _.intersectionBy(allowedLimitVars, limitVars, l => l.variableName);
      }
    });

    return allowedLimitVars;
  };

  handleSubScenarioClick = (index: number) => {
    this.setState({
      subScenarioIndex: index,
    });
  };

  handleDeleteSubScenario = (index: number) => {
    const updatedModel = _.cloneDeep(this.props.model);
    const scenarios = _.get(updatedModel, `nodes.${this.state.currentNode}.data.scenarios`, []);
    scenarios.splice(index, 1);
    _.set(updatedModel, `nodes.${this.state.currentNode}.data.scenarios`, scenarios);
    this.props.onModelChange(updatedModel);
  };

  handleAddScenario = () => {
    const newScenario = {
      name: 'new scenario',
      eventType: '',
      condition: null,
    };
    const updatedModel = _.cloneDeep(this.props.model);
    const scenarios = _.get(updatedModel, `nodes.${this.state.currentNode}.data.scenarios`, []);
    const newScenarioIndex = scenarios.length;
    _.set(updatedModel, `nodes.${this.state.currentNode}.data.scenarios`, [...scenarios, newScenario]);
    this.props.onModelChange(updatedModel);
    this.setState({
      subScenarioIndex: newScenarioIndex,
    });
  };

  onSubScenarioChange = (index: number, scenario: any) => {
    const updatedModel = _.cloneDeep(this.props.model);
    _.set(updatedModel, `nodes.${this.state.currentNode}.data.scenarios[${index}]`, scenario);
    this.props.onModelChange(updatedModel);
  };

  // getAllowedLimitTypes returns the limit types that are supported by all the scenario event types
  getAllowedLimitTypes = () => {
    const { model, listRuleTypes } = this.props;
    const allEventTypes = _.get(model, `nodes.${this.state.currentNode}.data.scenarios`, []).map(s => s.eventType);
    let allowedLimitTypes = [];

    _.map(allEventTypes, eventType => {
      const eventTypeConfig = _.get(listRuleTypes, model.targetUserType, []).find(
        eventData => _.get(eventData, 'eventType', '') === eventType,
      );
      const limitConfigs = _.get(eventTypeConfig, 'limit.scenario', []);
      if (allowedLimitTypes.length === 0) {
        allowedLimitTypes = limitConfigs;
      } else {
        allowedLimitTypes = _.intersectionBy(allowedLimitTypes, limitConfigs, l => l.key);
      }
    });

    return allowedLimitTypes;
  };

  getSubScenarioAccordinTitle = (subScenario: any) => {
    if (!_.isEmpty(subScenario.name)) {
      return subScenario.name;
    }
    if (!_.isEmpty(subScenario.eventType)) {
      return subScenario.eventType;
    }
    return 'Scenario';
  };

  render() {
    const { model, listRuleTypes } = this.props;
    const { currentNode, subScenarioIndex } = this.state;
    const targetUserType = _.get(model, 'targetUserType', '');

    const isRecordLastOccurrence = !_.isEmpty(_.get(model, `nodes.${currentNode}.recordLastOccurrenceKey`));
    const disableCheckBox = _.get(model, `nodes.${currentNode}.children`, []).length > 0;
    const sameDateChecked = _.get(model, `nodes.${currentNode}.data.meta.isSameDateChecked`, false);
    const autoHoldbackEnabled = _.get(model, `nodes.${currentNode}.autoHoldbackEnabled`, false);

    let allLimitTypes = [];
    const subScenarios = _.get(model, `nodes[${currentNode}].data.scenarios`, []);
    _.map(subScenarios, s => {
      const eventTypeData = _.get(listRuleTypes, targetUserType, []).find(
        eventData => _.get(eventData, 'eventType', '') === s.eventType,
      );
      const limits = _.get(eventTypeData, 'limit.scenario', []);
      allLimitTypes = _.unionBy(allLimitTypes, limits, l => l.key);
    });

    const allEventTypes = subScenarios.map(s => s.eventType);

    const limits = {
      scenario: allLimitTypes,
    };
    const bottomPlaceHolderStyle = {
      height: '200px',
    };

    return (
      <div>
        <div className="model-button">
          <Grid>
            <Grid.Column textAlign="right">
              <Button.Group>
                <Button
                  onClick={this.cancelScenario}
                >
                  Cancel
                </Button>
                <Button.Or />
                <Button
                  positive
                  onClick={this.saveScenario}
                >
                  Save
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid>
        </div>
        <div className="treatment-form">
          {/* Time Range */}
          <Grid>
            <Grid.Column width={8}>
              <Form>
                <Grid columns="equal">
                  <Grid.Column>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.meta.startTime`}
                      {...this.props}
                    >
                      <CDatePicker
                        required
                        selectsStart
                        showTimeSelect
                        label={i18n.t('common.fields.startDate')}
                        minDate={model.startTime}
                        maxDate={model.endTime}
                        selected={_.get(model.nodes[currentNode].data, 'meta.startTime')}
                        startDate={_.get(model.nodes[currentNode].data, 'meta.startTime')}
                        endDate={_.get(model.nodes[currentNode].data, 'meta.endTime')}
                        utcOffset={_.get(model, 'utcOffset')}
                        handleInputChange={this.props.handleInputChange}
                        disabled={sameDateChecked}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Grid.Column>
                  <Grid.Column>
                    <FormInputWithError
                      name={`nodes.${currentNode}.data.meta.endTime`}
                      {...this.props}
                    >
                      <CDatePicker
                        required
                        selectsEnd
                        showTimeSelect
                        label={i18n.t('common.fields.endDate')}
                        minDate={_.get(model.nodes[currentNode].data, 'meta.startTime') || model.startTime}
                        maxDate={model.endTime}
                        selected={_.get(model.nodes[currentNode].data, 'meta.endTime')}
                        startDate={_.get(model.nodes[currentNode].data, 'meta.startTime')}
                        endDate={_.get(model.nodes[currentNode].data, 'meta.endTime')}
                        utcOffset={_.get(model, 'utcOffset')}
                        handleInputChange={this.props.handleInputChange}
                        disabled={sameDateChecked}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Grid.Column>
                </Grid>
              </Form>
            </Grid.Column>
          </Grid>
          <Grid>
            <Grid.Column width={5}>
              <Form.Field>
                <Checkbox
                  name="same_dates"
                  label={i18n.t('targets_page.fields.same_as_campaign_dates')}
                  onChange={this.handleCheckSameDatesChange}
                  checked={sameDateChecked}
                />
              </Form.Field>
            </Grid.Column>
            {
              (targetUserType === targetUserTypes.pax || targetUserType === targetUserTypes.dax) &&
              <Grid.Column width={5}>
                <Form.Field>
                  <Checkbox
                    name={`nodes.${currentNode}.autoHoldbackEnabled`}
                    onChange={this.props.handleInputChange}
                    checked={autoHoldbackEnabled}
                    label="Auto Add Control Group"
                    disabled={this.isEdit()}
                  />
                  <Popup
                    trigger={<Icon name="question circle outline" style={{ marginLeft: 3 }} />}
                    position="bottom left"
                    hoverable
                  >
                    <React.Fragment>
                      <p>This will allow Trident to create experiment node automatically with a Control Group (10%) & one Treatment Group (90%) split. You can learn further more at <a href="https://wiki.grab.com/pages/viewpage.action?pageId=326902276" target="_blank" rel="noopener noreferrer">HERE</a></p>
                    </React.Fragment>
                  </Popup>
                </Form.Field>
              </Grid.Column>
            }
            <Grid.Column width={5}>
              <Form.Field>
                <Checkbox
                  name="recordLastOccurrence"
                  label="Record last occurrence"
                  onChange={this.handleLastOccurrenceChange}
                  checked={isRecordLastOccurrence}
                  disabled={disableCheckBox}
                />
              </Form.Field>
            </Grid.Column>
          </Grid>

          {/* Limits */}
          <Grid columns="equal">
            <Grid.Column width={12}>
              <Header as="h3">Limits</Header>
            </Grid.Column>
          </Grid>
          {!_.isEmpty(_.get(this.props.error, 'limits', [])) &&
            <Message negative size="tiny">
              <Message.Header>
                <span className="validation-icon" />
                {this.props.error.limits[0]}
              </Message.Header>
            </Message>
          }
          <Grid columns="equal">
            <Grid.Column width={16}>
              <LimitsTable
                {...this.props}
                pageName="Scenario"
                limits={limits}
                eventTypes={allEventTypes}
              />
            </Grid.Column>
          </Grid>

          {/* Scenarios */}
          <Segment className="treatment-form-segment">
            {
              (!this.isEdit() && subScenarios.length < MAX_SUBSCENARIOS) &&
              <div>
                <Button positive onClick={this.handleAddScenario}>Add Scenario</Button>
                <Divider />
              </div>
            }
            <Accordion fluid styled>
              {subScenarios.map((subScenario, index) => {
                return (
                  <div key={index}>
                    <Accordion.Title active={subScenarioIndex === index} index={index} onClick={() => this.handleSubScenarioClick(index)} key={`${index}'-subScenario-title`}>
                      <Icon name="dropdown" />
                      {this.getSubScenarioAccordinTitle(subScenario)}
                    </Accordion.Title>
                    <Accordion.Content active={subScenarioIndex === index} key={`${index}-subScenario-content`}>
                      <SubScenarioForm
                        model={subScenario}
                        targetUserType={targetUserType}
                        cities={this.props.cities}
                        country={this.props.country}
                        scenarioIndex={subScenarioIndex}
                        onModelChange={(scn) => {
                          this.onSubScenarioChange(index, scn);
                        }}
                        listRuleTypes={listRuleTypes}
                        isEdit={this.isEdit()}
                      />
                      <Divider />
                      <Grid columns="equal">
                        <Grid.Column width={8}>
                          {
                            subScenarios.length > 1 &&
                            <Label
                              size="small"
                              color="violet"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => this.handleDeleteSubScenario(index)}
                            >
                              <Icon name="trash alternate" /> Delete
                            </Label>
                          }
                        </Grid.Column>
                      </Grid>
                    </Accordion.Content>
                  </div>
                );
              })
              }
            </Accordion>
          </Segment>
          <p style={bottomPlaceHolderStyle} />
        </div>
      </div>
    );
  }
}
